import React, { useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PendingTask from "./TabFiles/PendingTask";
import DueList from "./TabFiles/Due";
import AssignedToMe from "./TabFiles/AssignedToMe";
import MissedDeadLineList from "./TabFiles/MissedDeadline";
import PendingActionTracker from "./actionTracker/PendingActionTracker";
import MissedDeadlineTracker from "./actionTracker/MissedDeadlineTracker";
import CriticalActionTracker from "./actionTracker/CriticalActionTracker";
import { useGetMyWorkQuery } from "store/slices/apis/workFlowSlice";
import { useAuth } from "context/AuthContext";
import EnterprisePageShell from "components/EnterprisePageShell";
import { enterpriseTokens } from "styles/theme";

const mainTabs = [
    { label: "Action Tracker", value: "actionTracker", description: "Operational actions requiring attention" },
    { label: "Workflow Approvals", value: "workflow", description: "Reviews, approvals, and assigned workflow tasks" },
];

const MyWorkDashboard = () => {
    const [queueTab, setQueueTab] = useState(0);
    const [mainTab, setMainTab] = useState("actionTracker");
    const { data: myWorkResponse, isLoading, error } = useGetMyWorkQuery();
    const { user } = useAuth();

    const workItems = useMemo(() => myWorkResponse?.data ?? [], [myWorkResponse?.data]);

    const workQueues = useMemo(() => {
        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();
        const isOpen = (item) => ["Pending", "In Progress", "Active"].includes(item.workflowStatus);

        return {
            pending: workItems.filter(isOpen),
            assigned: workItems.filter((item) => item.approverRole === "5e9f8e5f-0fec-4035-824b-85d9c33b1939"),
            dueThisMonth: workItems.filter((item) => {
                const dueDate = new Date(item.dueDate);
                return isOpen(item) && dueDate.getMonth() === month && dueDate.getFullYear() === year;
            }),
            overdue: workItems.filter((item) => new Date(item.dueDate) < now),
        };
    }, [workItems]);

    const metrics = [
        {
            label: "Pending review",
            value: workQueues.pending.length,
            note: "Open workflow items",
            icon: AssignmentOutlinedIcon,
            color: enterpriseTokens.primary,
            bg: "#EFF6FF",
        },
        {
            label: "Assigned to me",
            value: workQueues.assigned.length,
            note: "Direct responsibility",
            icon: PersonOutlineOutlinedIcon,
            color: enterpriseTokens.accent,
            bg: "#ECFEFF",
        },
        {
            label: "Due this month",
            value: workQueues.dueThisMonth.length,
            note: "Upcoming commitments",
            icon: CalendarMonthOutlinedIcon,
            color: enterpriseTokens.warning,
            bg: "#FFFBEB",
        },
        {
            label: "Past deadline",
            value: workQueues.overdue.length,
            note: "Requires intervention",
            icon: ErrorOutlineOutlinedIcon,
            color: enterpriseTokens.danger,
            bg: "#FEF2F2",
        },
    ];

    const workflowTabs = [
        { label: "Pending", count: workQueues.pending.length },
        { label: "Assigned to me", count: workQueues.assigned.length },
        { label: "Due this month", count: workQueues.dueThisMonth.length },
        { label: "Past deadline", count: workQueues.overdue.length },
    ];

    const actionTabs = [
        { label: "Pending actions" },
        { label: "Past deadline" },
        ...(user?.roles?.includes("Critical Action Viewer") ? [{ label: "Critical items" }] : []),
    ];

    const changeMainTab = (_, nextTab) => {
        setMainTab(nextTab);
        setQueueTab(0);
    };

    return (
        <EnterprisePageShell
            eyebrow="Personal Work Command Center"
            title="My Workspace"
            description="Prioritize actions, review approvals, and focus on commitments that need intervention."
            meta={
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip label={`${workItems.length} workflow items`} sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.16)", fontWeight: 600 }} />
                    {workQueues.overdue.length > 0 && (
                        <Chip label={`${workQueues.overdue.length} overdue`} sx={{ bgcolor: "rgba(254,226,226,0.14)", color: "#FECACA", border: "1px solid rgba(254,202,202,0.22)", fontWeight: 600 }} />
                    )}
                </Box>
            }
        >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }, gap: 1.5, mb: 2 }}>
                {metrics.map((metric) => {
                    const Icon = metric.icon;
                    return (
                        <Box
                            key={metric.label}
                            sx={{
                                minHeight: 96,
                                p: 1.5,
                                bgcolor: "#FFFFFF",
                                border: `1px solid ${enterpriseTokens.lineSoft}`,
                                borderRadius: "3px",
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 2,
                                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: enterpriseTokens.muted, textTransform: "uppercase" }}>
                                    {metric.label}
                                </Typography>
                                <Typography sx={{ mt: 0.4, fontSize: 20, lineHeight: "25px", fontWeight: 600, color: enterpriseTokens.navy }}>
                                    {isLoading ? "-" : metric.value}
                                </Typography>
                                <Typography sx={{ mt: 0.5, fontSize: 12, fontWeight: 600, color: enterpriseTokens.muted }}>
                                    {metric.note}
                                </Typography>
                            </Box>
                            <Box sx={{ width: 32, height: 32, borderRadius: "3px", bgcolor: metric.bg, color: metric.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Icon fontSize="small" />
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ bgcolor: "#FFFFFF", border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: "3px", overflow: "hidden", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)" }}>
                <Box sx={{ p: 1.25, bgcolor: "#F8FAFC", borderBottom: `1px solid ${enterpriseTokens.lineSoft}` }}>
                    <Tabs
                        value={mainTab}
                        onChange={changeMainTab}
                        TabIndicatorProps={{ style: { display: "none" } }}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{ minHeight: 0, "& .MuiTabs-flexContainer": { gap: 1 } }}
                    >
                        {mainTabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                disableRipple
                                label={
                                    <Box sx={{ textAlign: "left" }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: mainTab === tab.value ? "#FFFFFF" : enterpriseTokens.navy }}>
                                            {tab.label}
                                        </Typography>
                                        <Typography sx={{ display: { xs: "none", md: "block" }, mt: 0.15, fontSize: 11, fontWeight: 600, color: mainTab === tab.value ? "#DBEAFE" : enterpriseTokens.muted }}>
                                            {tab.description}
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    minHeight: 44,
                                    minWidth: { xs: 140, md: 220 },
                                    alignItems: "flex-start",
                                    px: 1.75,
                                    py: 1,
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    color: mainTab === tab.value ? "#FFFFFF" : enterpriseTokens.slate,
                                    bgcolor: mainTab === tab.value ? enterpriseTokens.primary : "transparent",
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 2 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: enterpriseTokens.navy }}>
                        {mainTab === "workflow" ? "Workflow queue" : "Action queue"}
                    </Typography>
                    <Typography sx={{ mt: 0.25, fontSize: 13, color: enterpriseTokens.muted }}>
                        {mainTab === "workflow" ? "Review tasks by ownership and delivery urgency." : "Track operational commitments by urgency and criticality."}
                    </Typography>

                    <Tabs
                        value={queueTab}
                        onChange={(_, nextTab) => setQueueTab(nextTab)}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{ mt: 1.5, minHeight: 38, borderBottom: `1px solid ${enterpriseTokens.lineSoft}`, "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" } }}
                    >
                        {(mainTab === "workflow" ? workflowTabs : actionTabs).map((tab) => (
                            <Tab
                                key={tab.label}
                                label={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        {tab.label}
                                        {tab.count !== undefined && <Chip label={tab.count} size="small" sx={{ height: 20, minWidth: 26, fontSize: 11, fontWeight: 600 }} />}
                                    </Box>
                                }
                                sx={{ minHeight: 38, px: 1.5, textTransform: "none", fontSize: 13, fontWeight: 600 }}
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box sx={{ p: { xs: 1.5, md: 2 }, pt: 1.5 }}>
                    {isLoading && mainTab === "workflow" && (
                        <Box sx={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress size={28} /></Box>
                    )}
                    {error && mainTab === "workflow" && (
                        <Box sx={{ p: 3, bgcolor: "#FEF2F2", borderRadius: "3px", color: enterpriseTokens.danger }}>Unable to load workflow items.</Box>
                    )}

                    {!isLoading && !error && mainTab === "workflow" && queueTab === 0 && <PendingTask data={workQueues.pending} />}
                    {!isLoading && !error && mainTab === "workflow" && queueTab === 1 && <AssignedToMe data={workQueues.assigned} />}
                    {!isLoading && !error && mainTab === "workflow" && queueTab === 2 && <DueList data={workQueues.dueThisMonth} />}
                    {!isLoading && !error && mainTab === "workflow" && queueTab === 3 && <MissedDeadLineList data={workQueues.overdue} />}

                    {mainTab === "actionTracker" && queueTab === 0 && <PendingActionTracker />}
                    {mainTab === "actionTracker" && queueTab === 1 && <MissedDeadlineTracker qParams={{ ShowOverdueOnly: true }} />}
                    {mainTab === "actionTracker" && queueTab === 2 && <CriticalActionTracker qParams={{ IsCritical: true }} />}
                </Box>
            </Box>
        </EnterprisePageShell>
    );
};

export default MyWorkDashboard;
